import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  getActiveTimeEntries, getJobTimeEntries, getTeamMembers, getWorkOrders,
  clockInCrew, clockOut, clockOutJob, pauseCrew, resumeCrew, splitTimeEntry,
  getAuthToken,
} from '../services/api';
import { API_URL, API_BASE_URL } from '../config';
import FieldChecklistModal from './FieldChecklistModal';
// FIELD_WORK_TYPES is the single source of truth for "was somebody on the property" —
// the modal gates its field questions on the same list, and the two drifting apart is
// how office work ended up being asked field questions.
import RequiredCommentModal, { FIELD_WORK_TYPES } from './RequiredCommentModal';

// Statuses offered in the post-clockout "Update Job Status?" modal — kept in sync with
// the same options the TimeTracking page shows.
const JOB_STATUS_OPTIONS = [
  { status: 'Pending', label: 'Pending', color: '#94a3b8' },
  { status: 'Scheduled', label: 'Scheduled', color: '#3b82f6' },
  { status: 'In Progress - Office Work', label: 'Office Work (pre-field)', color: '#0ea5e9' },
  { status: 'In Progress - Initial Field Work', label: 'Initial Field Work', color: '#a78bfa' },
  { status: 'In Progress - Topo', label: 'Topo', color: '#6366f1' },
  { status: 'In Progress - Additional Field Work Required', label: 'Additional Field Work Required', color: '#7c3aed' },
  { status: 'In Progress - Drafting', label: 'Ready for Drafting', color: '#8b5cf6' },
  { status: 'Survey Complete', label: 'Survey Complete', color: '#34d399' },
  { status: 'Needs Review', label: 'Needs Review', color: '#f97316' },
  { status: 'Sent for Review', label: 'Sent for Review', color: '#fb923c' },
  { status: 'Approved - Internal Review', label: 'Approved (our office)', color: '#4ade80' },
  { status: 'Approved - Planning & Zoning', label: 'Approved (Planning & Zoning)', color: '#22c55e' },
  { status: 'Submitted for Recording', label: 'Submitted for Recording', color: '#14b8a6' },
  { status: 'Recorded', label: 'Recorded', color: '#0d9488' },
  { status: 'Final Mark Up / Stake Lines', label: 'Final Mark Up / Stake Lines', color: '#ec4899' },
  { status: 'Invoiced', label: 'Invoiced', color: '#f59e0b' },
  { status: 'Paid', label: 'Paid', color: '#16a34a' },
];

// Matches WORK_TYPE_OPTIONS used elsewhere (Field Work, Drafting, etc.)
const WORK_TYPES = [
  'Field Work', 'Drafting', 'Final Mark Up / Stake Lines',
  'Office Work', 'Research', 'Travel', 'Review',
];

function fmt(totalSec) {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

// Live elapsed seconds for an open entry (backend stores UTC; append 'Z')
function entrySeconds(e, nowMs) {
  const base = e.accumulated_seconds || 0;
  if (e.is_paused) return base;
  const startStr = e.calc_clock_in || e.clock_in;
  if (!startStr) return base;
  const start = new Date(startStr + (startStr.endsWith('Z') ? '' : 'Z')).getTime();
  return base + Math.max(0, (nowMs - start) / 1000);
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function woLabel(wo) {
  const num = wo.order_number || `WO-${wo.id}`;
  return wo.description ? `${num} — ${wo.description}` : num;
}

export default function JobTimer({ jobId, jobNumber, jobStatus, phases, onStatusChanged }) {
  const { user } = useAuth();
  const [active, setActive] = useState([]);     // viewer's own/group active entries
  const [jobOpen, setJobOpen] = useState([]);   // everyone currently clocked into THIS job
  const [members, setMembers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [sheet, setSheet] = useState(false);
  const [workType, setWorkType] = useState('');
  const [helpers, setHelpers] = useState([]);
  const [selWO, setSelWO] = useState('');
  const [selPhase, setSelPhase] = useState('');
  // Only offer phases still being worked (hide Paid/Complete/Cancelled), so a finished
  // Design phase drops off once construction is active.
  const activePhases = (phases || []).filter(p => !['Paid', 'Cancelled', 'Complete', 'Survey Complete'].includes(p.status));
  const [woFiles, setWoFiles] = useState([]);   // attachments on the selected work order
  const [split, setSplit] = useState(null);     // { entryId, total, allocs: {woId: hoursString} }
  const [stopChoice, setStopChoice] = useState(false); // crew clocked in together: stop all vs just me

  // Post-clockin/clockout modal flow (mirrors TimeTracking page)
  const [fieldChecklistOverview, setFieldChecklistOverview] = useState(null); // {job_id, job_number} on start
  const [fieldChecklistClockout, setFieldChecklistClockout] = useState(null); // {job_id, job_number} on stop
  const [statusUpdateJob, setStatusUpdateJob] = useState(null);                // {job_id, job_number, job_status}
  const [commentPrompt, setCommentPrompt] = useState(null);                    // {job_id, job_number} — required note after stop
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, j] = await Promise.all([
        getActiveTimeEntries(),
        jobId ? getJobTimeEntries(jobId) : Promise.resolve({ data: {} }),
      ]);
      setActive(a.data?.time_entries || []);
      setJobOpen((j.data?.time_entries || []).filter(e => !e.clock_out));
    } catch (e) { /* leave as-is */ }
  }, [jobId]);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    getTeamMembers().then(r => {
      const l = r.data?.team_members || r.data?.members || (Array.isArray(r.data) ? r.data : []);
      setMembers(l);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!jobId) return;
    getWorkOrders(jobId).then(r => setWorkOrders(r.data?.work_orders || [])).catch(() => {});
  }, [jobId]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  // Load the selected work order's attachments (crew drawings) so they show in the Start sheet.
  useEffect(() => {
    if (!selWO || !jobId) { setWoFiles([]); return; }
    let active = true;
    (async () => {
      try {
        const token = await getAuthToken();
        const res = await axios.get(`${API_URL}/jobs/${jobId}/attachments?work_order_id=${selWO}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (active) setWoFiles(res.data?.attachments || []);
      } catch (e) { if (active) setWoFiles([]); }
    })();
    return () => { active = false; };
  }, [selWO, jobId]);

  const openWoFile = async (att) => {
    const token = await getAuthToken();
    window.open(`${API_BASE_URL || ''}${att.view_url}?token=${encodeURIComponent(token)}`, '_blank');
  };

  if (!user) return null;

  const myOpen = active.filter(e => e.team_member_id === user.id && !e.clock_out);
  const mine = jobOpen.find(e => e.team_member_id === user.id) || myOpen.find(e => e.job_id === jobId);
  const otherJob = myOpen.find(e => e.job_id && e.job_id !== jobId);
  // open work orders only (don't offer completed/cancelled WOs for new time)
  const openWOs = workOrders.filter(w => !['Completed', 'Cancelled', 'Paid'].includes(w.status));
  // On a phased job, once a phase is picked, only show that phase's work orders (plus any
  // with no phase assigned) in the clock-in picker.
  const sheetWOs = selPhase ? openWOs.filter(w => !w.phase_id || String(w.phase_id) === String(selPhase)) : openWOs;

  const openSheet = () => {
    setErr(''); setWorkType(''); setHelpers([]);
    const t = todayStr();
    const todays = openWOs.find(w => w.scheduled_date === t);
    setSelWO(todays ? String(todays.id) : '');
    // Auto-select the phase only when there's exactly one live phase; otherwise force a pick.
    setSelPhase(activePhases.length === 1 ? String(activePhases[0].id) : '');
    setSheet(true);
  };
  const toggleHelper = (id) => setHelpers(h => h.includes(id) ? h.filter(x => x !== id) : [...h, id]);

  const start = async () => {
    if (!workType) { setErr('Pick a work type first.'); return; }
    if (activePhases.length > 1 && !selPhase) { setErr('Pick which phase you\'re working.'); return; }
    setBusy(true); setErr('');
    try {
      if (otherJob) { await clockOut(otherJob.id); }
      await clockInCrew({
        job_id: jobId,
        team_member_ids: [user.id, ...helpers],
        work_type: workType,
        ...(selWO ? { work_order_id: Number(selWO) } : {}),
        ...(selPhase ? { phase_id: Number(selPhase) } : {}),
      });
      setSheet(false); await load();
      // On field-work start, show the field checklist overview so the crew sees
      // what they need to cover before leaving the office (same as Time Clock page).
      if (FIELD_WORK_TYPES.includes(workType)) {
        setFieldChecklistOverview({ job_id: jobId, job_number: jobNumber });
      }
    } catch (e) { setErr('Could not start the timer. Try again.'); }
    setBusy(false);
  };

  // After clock-out: for field work, open the field checklist (clockout mode); for
  // non-field work, jump straight to the status update modal. Matches Time Clock flow.
  const showPostClockOutFlow = (workTypeUsed) => {
    if (FIELD_WORK_TYPES.includes(workTypeUsed)) {
      setFieldChecklistClockout({ job_id: jobId, job_number: jobNumber, work_type: workTypeUsed });
    } else {
      setStatusUpdateJob({ job_id: jobId, job_number: jobNumber, job_status: jobStatus, work_type: workTypeUsed });
    }
  };

  // Field checklist finished/closed → open status update modal next
  const handleFieldChecklistDone = () => {
    setFieldChecklistClockout(currentJob => {
      if (currentJob) {
        setStatusUpdateJob({ job_id: currentJob.job_id, job_number: currentJob.job_number, job_status: jobStatus, work_type: currentJob.work_type });
      }
      return null;
    });
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!statusUpdateJob) return;
    const j = statusUpdateJob;
    try {
      const token = await getAuthToken();
      const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      await axios.put(`${API_URL}/jobs/${j.job_id}`, { status: newStatus }, config);
      if (onStatusChanged) onStatusChanged(newStatus);
    } catch (e) {
      console.error('Failed to update job status:', e);
    }
    setStatusUpdateJob(null);
    setCommentPrompt({ job_id: j.job_id, job_number: j.job_number, work_type: j.work_type });
  };

  // Crew sharing this user's clock-in group on this job — who "Stop the whole crew" affects.
  const crewGroup = mine && mine.clock_in_group
    ? jobOpen.filter(e => e.clock_in_group === mine.clock_in_group && !e.clock_out)
    : (mine ? [mine] : []);
  const crewOthers = crewGroup.filter(e => e.team_member_id !== user.id);

  // If the job has 2+ open work orders, offer to split the logged time across them.
  const offerSplitFor = (entry) => {
    if (openWOs.length >= 2 && (entry?.total_hours || 0) > 0) {
      const total = entry.total_hours;
      const per = Math.round((total / openWOs.length) * 100) / 100;
      const allocs = {};
      let acc = 0;
      openWOs.forEach((w, i) => {
        const v = i === openWOs.length - 1 ? Math.round((total - acc) * 100) / 100 : per;
        allocs[w.id] = String(v); acc += v;
      });
      setSplit({ entryId: entry.id, total, allocs });
    }
  };

  const stop = () => {
    if (!mine) return;
    // Clocked in with others? Ask whether to stop the whole crew or just leave the job yourself.
    if (crewOthers.length > 0) { setStopChoice(true); return; }
    doStopMine();
  };

  const doStopMine = async () => {
    if (!mine) return;
    const stoppedWorkType = mine.work_type; // capture before clear
    setStopChoice(false); setBusy(true);
    try {
      const resp = await clockOut(mine.id);
      offerSplitFor(resp.data || {});
      await load();
      showPostClockOutFlow(stoppedWorkType);
    } catch (e) {
      alert('Could not stop the timer. Please try again.');
    }
    setBusy(false);
  };

  const doStopCrew = async () => {
    const stoppedWorkType = mine?.work_type;
    setStopChoice(false); setBusy(true);
    try {
      const resp = await clockOutJob(jobId);
      const entries = resp.data?.entries || [];
      const myEntry = entries.find(e => e.team_member_id === user.id) || {};
      offerSplitFor(myEntry);
      await load();
      showPostClockOutFlow(stoppedWorkType);
    } catch (e) {
      alert('Could not stop the crew. Please try again.');
    }
    setBusy(false);
  };

  const pauseResume = async () => {
    if (!mine) return; setBusy(true);
    // Pause/resume the whole crew clocked in together, not just this person.
    try { mine.is_paused ? await resumeCrew(jobId) : await pauseCrew(jobId); await load(); } catch (e) {}
    setBusy(false);
  };

  const setAlloc = (woId, val) => setSplit(s => ({ ...s, allocs: { ...s.allocs, [woId]: val } }));
  const splitSum = split ? Object.values(split.allocs).reduce((a, v) => a + (parseFloat(v) || 0), 0) : 0;
  const splitRemaining = split ? Math.round((split.total - splitSum) * 100) / 100 : 0;
  const splitNonZero = split ? Object.values(split.allocs).filter(v => (parseFloat(v) || 0) > 0).length : 0;
  const splitOk = split && Math.abs(splitRemaining) <= 0.01 && splitNonZero >= 2;

  const applySplit = async () => {
    if (!splitOk) return;
    setBusy(true);
    try {
      const allocations = openWOs
        .map(w => ({ work_order_id: w.id, hours: parseFloat(split.allocs[w.id]) || 0 }))
        .filter(a => a.hours > 0);
      await splitTimeEntry(split.entryId, allocations);
      setSplit(null); await load();
    } catch (e) { setErr('Could not split the time. It was saved as one entry.'); setSplit(null); }
    setBusy(false);
  };

  const card = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff' };
  const startBtn = { width: '100%', padding: '14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer' };

  // Modals are portaled to <body> so they render correctly across all of this
  // component's early-return paths (idle / active / stop-choice / split-sheet).
  const modals = (typeof document !== 'undefined') ? createPortal((
    <>
      {/* Field checklist — overview shown on timer start for field work */}
      {fieldChecklistOverview && (
        <FieldChecklistModal
          jobId={fieldChecklistOverview.job_id}
          jobNumber={fieldChecklistOverview.job_number}
          mode="overview"
          onComplete={() => setFieldChecklistOverview(null)}
          onClose={() => setFieldChecklistOverview(null)}
        />
      )}
      {/* Field checklist — clockout mode after stopping a field-work timer */}
      {fieldChecklistClockout && (
        <FieldChecklistModal
          jobId={fieldChecklistClockout.job_id}
          jobNumber={fieldChecklistClockout.job_number}
          mode="clockout"
          onComplete={handleFieldChecklistDone}
          onClose={handleFieldChecklistDone}
        />
      )}
      {/* Status update modal — opens after the field checklist or directly for non-field work */}
      {statusUpdateJob && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 999999, padding: '16px',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '20px',
            maxWidth: '400px', width: '100%', textAlign: 'center',
            maxHeight: 'calc(90dvh - 90px)', overflowY: 'auto',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '17px' }}>Update Job Status?</h3>
            <p style={{ color: '#666', margin: '0 0 12px', fontSize: '14px' }}>
              <strong>{statusUpdateJob.job_number}</strong>
            </p>
            <p style={{ color: '#999', margin: '0 0 14px', fontSize: '12px' }}>
              Currently: {statusUpdateJob.job_status || 'Unknown'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px', maxHeight: '60dvh', overflowY: 'auto' }}>
              {JOB_STATUS_OPTIONS.filter(opt => opt.status !== statusUpdateJob.job_status).map(opt => (
                <button
                  key={opt.status}
                  onClick={() => handleStatusUpdate(opt.status)}
                  style={{
                    padding: '12px 14px', border: '2px solid #e5e7eb', borderRadius: '10px',
                    backgroundColor: 'white', cursor: 'pointer', fontSize: '14px',
                    fontWeight: '500', textAlign: 'left', display: 'flex',
                    alignItems: 'center', gap: '10px',
                  }}
                >
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: opt.color, flexShrink: 0 }} />
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => { const j = statusUpdateJob; setStatusUpdateJob(null); setCommentPrompt({ job_id: j.job_id, job_number: j.job_number, work_type: j.work_type }); }}
              style={{
                padding: '10px 24px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666', width: '100%',
              }}
            >
              Skip — No Change
            </button>
          </div>
        </div>
      )}
      {/* Required job note — must be filled before the stop flow closes */}
      {commentPrompt && (
        <RequiredCommentModal
          jobId={commentPrompt.job_id}
          jobNumber={commentPrompt.job_number}
          workType={commentPrompt.work_type}
          onDone={() => setCommentPrompt(null)}
        />
      )}
    </>
  ), document.body) : null;

  // ---- STOP CHOICE (you clocked in with a crew) ----
  if (stopChoice) {
    const n = crewGroup.length;
    return (
      <>{modals}
      <div style={{ ...card, borderColor: '#dc2626' }}>
        <div style={{ fontWeight: 700, color: '#0f172a' }}>Stop the timer</div>
        <div style={{ fontSize: 13, color: '#64748b', margin: '4px 0 14px' }}>
          You clocked in with a crew of {n}. Stop everyone, or just clock yourself out and leave the rest running?
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button disabled={busy} onClick={doStopCrew} style={{ padding: '13px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
            ⏹ Stop the whole crew ({n})
          </button>
          <button disabled={busy} onClick={doStopMine} style={{ padding: '13px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
            Just clock me out
          </button>
          <button disabled={busy} onClick={() => setStopChoice(false)} style={{ padding: '10px', background: 'transparent', color: '#64748b', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
        </div>
      </div>
      </>
    );
  }

  // ---- SPLIT SHEET (after Stop, when job has 2+ work orders) ----
  if (split) {
    return (
      <>{modals}
      <div style={{ ...card, borderColor: '#2563eb' }}>
        <div style={{ fontWeight: 700, color: '#0f172a' }}>Split your time across work orders</div>
        <div style={{ fontSize: 13, color: '#64748b', margin: '4px 0 12px' }}>
          You logged <strong>{split.total.toFixed(2)} hrs</strong>. Enter how much went to each work order.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {openWOs.map(w => (
            <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, fontSize: 13, color: '#334155', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{woLabel(w)}</div>
              <input type="number" step="0.25" min="0" value={split.allocs[w.id]} onChange={e => setAlloc(w.id, e.target.value)}
                style={{ width: 72, padding: '8px', border: '1px solid #cbd5e1', borderRadius: 8, textAlign: 'right' }} />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>hrs</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, marginTop: 10, color: Math.abs(splitRemaining) <= 0.01 ? '#16a34a' : '#dc2626' }}>
          {Math.abs(splitRemaining) <= 0.01 ? '✓ Adds up' : `${splitRemaining > 0 ? splitRemaining.toFixed(2) + ' hrs left to assign' : Math.abs(splitRemaining).toFixed(2) + ' hrs over'}`}
        </div>
        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 6 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button disabled={busy} onClick={() => setSplit(null)} style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Keep as one entry</button>
          <button disabled={busy || !splitOk} onClick={applySplit} style={{ ...startBtn, flex: 2, opacity: (busy || !splitOk) ? 0.6 : 1 }}>
            {busy ? 'Splitting…' : 'Apply Split'}
          </button>
        </div>
      </div>
      </>
    );
  }

  // ---- "On this job now" panel ----
  const panel = jobOpen.length > 0 ? (
    <div style={{ ...card, marginTop: 10, padding: 12 }}>
      <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        On this job now · {jobOpen.length}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {jobOpen.map(e => {
          const isMe = e.team_member_id === user.id;
          return (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.is_paused ? '#f59e0b' : '#16a34a', flexShrink: 0 }} />
                <span style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.member_name || e.team_member_name || 'Crew member'}{isMe ? ' (you)' : ''}
                </span>
                {e.work_type && <span style={{ fontSize: 12, color: '#64748b' }}>· {e.work_type}</span>}
                {e.work_order_number && <span style={{ fontSize: 12, color: '#2563eb' }}>· {e.work_order_number}</span>}
                {e.is_paused && <span style={{ fontSize: 11, color: '#b45309' }}>paused</span>}
              </div>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: e.is_paused ? '#b45309' : '#15803d' }}>
                {fmt(entrySeconds(e, now))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  // ---- RUNNING ----
  if (mine) {
    const secs = entrySeconds(mine, now);
    return (
      <>
        <div style={{ ...card, borderColor: mine.is_paused ? '#f59e0b' : '#16a34a' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {mine.is_paused ? 'Paused' : 'On the clock'} · {mine.work_type || 'Work'}{mine.work_order_number ? ` · ${mine.work_order_number}` : ''}
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: mine.is_paused ? '#b45309' : '#15803d' }}>
                {fmt(secs)}
              </div>
            </div>
            <div style={{ fontSize: 28 }}>{mine.is_paused ? '⏸️' : '⏱️'}</div>
          </div>
          {openWOs.length >= 2 && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>You can split this time across work orders when you stop.</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button disabled={busy} onClick={pauseResume} style={{ flex: 1, padding: '12px', background: mine.is_paused ? '#16a34a' : '#f1f5f9', color: mine.is_paused ? '#fff' : '#0f172a', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
              {mine.is_paused ? '▶ Resume' : '⏸ Pause'}
            </button>
            <button disabled={busy} onClick={stop} style={{ flex: 1, padding: '12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
              ⏹ Stop
            </button>
          </div>
        </div>
        {panel}
        {modals}
      </>
    );
  }

  // ---- START SHEET ----
  if (sheet) {
    return (
      <>{modals}
      <div style={card}>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Start a timer{jobNumber ? ` on ${jobNumber}` : ''}</div>
        {otherJob && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: '8px 10px', fontSize: 13, marginBottom: 10 }}>
            You're still clocked in on <strong>{otherJob.job_number || `job #${otherJob.job_id}`}</strong>. Starting here will clock you out of it.
          </div>
        )}
        {activePhases.length > 0 && (
          <div style={{ marginBottom: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#3730a3', marginBottom: 6 }}>
              Which phase are you working? {activePhases.length > 1 && <span style={{ color: '#dc2626' }}>*</span>}
            </div>
            <select value={selPhase} onChange={e => { setSelPhase(e.target.value); setSelWO(''); setErr(''); }}
              style={{ width: '100%', padding: '10px', border: '1px solid #c7d2fe', borderRadius: 8, fontSize: 14, background: '#fff' }}>
              <option value="">— Choose a phase —</option>
              {activePhases.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` — ${p.client_name}` : ''}</option>
              ))}
            </select>
            <div style={{ fontSize: 11.5, color: '#4338ca', marginTop: 5 }}>Your time bills to this phase's client.</div>
          </div>
        )}
        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', margin: '6px 0' }}>What's this timer for? <span style={{ color: '#dc2626' }}>*</span></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {WORK_TYPES.map(w => (
            <button key={w} onClick={() => { setWorkType(w); setErr(''); }} style={{ padding: '8px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: workType === w ? '#2563eb' : '#cbd5e1', background: workType === w ? '#2563eb' : '#fff', color: workType === w ? '#fff' : '#334155' }}>
              {w}
            </button>
          ))}
        </div>

        {sheetWOs.length > 0 && (
          <>
            <div style={{ marginTop: 12, background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: 10, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#92400e', lineHeight: 1.4 }}>
                This job has {sheetWOs.length} work order{sheetWOs.length > 1 ? 's' : ''}{selPhase ? ' for this phase' : ''} — pick the one you're working on below so your time and its files attach to it.
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', margin: '10px 0 6px' }}>Work order <span style={{ fontWeight: 400, color: '#94a3b8' }}>(you can split later)</span></div>
            <select value={selWO} onChange={e => setSelWO(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, background: '#fff' }}>
              <option value="">— No specific work order —</option>
              {sheetWOs.map(w => (
                <option key={w.id} value={w.id}>{woLabel(w)}{w.scheduled_date === todayStr() ? '  (today)' : ''}</option>
              ))}
            </select>
            {selWO && woFiles.length > 0 && (
              <div style={{ marginTop: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  📎 {woFiles.length} file{woFiles.length > 1 ? 's' : ''} on this work order
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {woFiles.map(a => (
                    <button key={a.id} type="button" onClick={() => openWoFile(a)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 9px', cursor: 'pointer' }}>
                      <span style={{ fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.original_filename || 'File'}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', flexShrink: 0 }}>View</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', margin: '12px 0 6px' }}>Who's helping? <span style={{ fontWeight: 400, color: '#94a3b8' }}>(you're already included)</span></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
          {members.filter(m => m.id !== user.id).map(m => (
            <button key={m.id} onClick={() => toggleHelper(m.id)} style={{ padding: '7px 11px', borderRadius: 20, fontSize: 13, cursor: 'pointer', border: '1px solid', borderColor: helpers.includes(m.id) ? '#16a34a' : '#cbd5e1', background: helpers.includes(m.id) ? '#dcfce7' : '#fff', color: '#334155' }}>
              {helpers.includes(m.id) ? '✓ ' : ''}{m.name}
            </button>
          ))}
        </div>
        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button disabled={busy} onClick={() => setSheet(false)} style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button disabled={busy || !workType} onClick={start} style={{ ...startBtn, flex: 2, opacity: (busy || !workType) ? 0.6 : 1 }}>
            {busy ? 'Starting…' : (otherJob ? 'Switch & Start' : 'Start Timer')}
          </button>
        </div>
      </div>
      </>
    );
  }

  // ---- IDLE ----
  return (
    <>
      <div style={card}>
        <button onClick={openSheet} style={startBtn}>⏱ Start Timer</button>
        {otherJob && (
          <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
            You're currently on {otherJob.job_number || `job #${otherJob.job_id}`}
          </div>
        )}
      </div>
      {panel}
      {modals}
    </>
  );
}
